import {PropsWithChildren, useMemo} from "react";
import {ProductAddOnGateProvider} from "../../../../components/offering-form/products/pricing/add-on-gate";
import {useDigitalProductAddOns} from "../../../../features/digital-products";
import {IS_PWYW_ENABLED} from "../../../../services/config";
import {useSaveAddOns} from "../../add-ons/use-save-add-ons";

/**
 * Tells the Pricing section what switching this product to Pay What You Want would cost.
 *
 * Edit only. In the create wizard the Pricing Model is chosen several steps before
 * Add-ons are ever offered, so there is nothing to warn about and no provider is mounted.
 *
 * Discarding reuses the ordinary add-ons save with an empty form: every persisted group
 * is absent from it, so the save resolves to a DELETE per group and nothing else.
 */
export function EditProductAddOnGate({
  productId,
  children,
}: PropsWithChildren<{productId: number}>) {
  // With the flag off there is no Pay What You Want to gate, so the fetch never happens
  // and Add-on behaviour is exactly as it is today.
  const addOnsQuery = useDigitalProductAddOns(IS_PWYW_ENABLED ? productId : undefined);
  const {saveAddOns} = useSaveAddOns(productId);

  const addOns = useMemo(() => addOnsQuery.data ?? [], [addOnsQuery.data]);

  const gate = useMemo(
    () => ({
      addOnCount: addOns.length,
      discardAddOns: async () => {
        await saveAddOns(addOns, {showVariants: false, variants: []});
      },
    }),
    // `saveAddOns` is a fresh closure every render but only ever reaches React Query's
    // stable `mutateAsync` functions, so re-deriving the gate for it would churn the
    // context for nothing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [addOns]
  );

  return <ProductAddOnGateProvider gate={gate}>{children}</ProductAddOnGateProvider>;
}
